import { Alert, Button, Form, theme, Typography, Space } from "antd";
import { PermissionForm } from "./CompanyPermissionFromDialog.default";
import { useEffect, useState } from "react";
import {
  addCompanyRolePermission,
  updateCompanyRolePermission,
  deleteCompanyRolePermission,
  getPermissions,
  getCompanies,
  getCompanyUserRoles,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import {
  PermissionActionType,
  Permission,
  CompanyPermission,
  Company,
  CompanyRole,
} from "../repository/data.default";
import { InputObject } from "../controls/default";
import { retry, getProblemMessage } from "../utils/http";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { hasFormTouched } from "../utils/hasFormChanged";
import { setFormFields } from "../utils/setFormFields";

export interface CompanyPermissionsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: CompanyPermission;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function CompanyPermissionsFormDialog(
  props: CompanyPermissionsFormDialogProps,
) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<PermissionForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { companyRole, action, permission } = values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addCompanyRolePermission({
        companyRoleUid: companyRole.value,
        allowedAction: action.value,
        permissionUid: permission.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateCompanyRolePermission({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          allowedAction: action.value,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteCompanyRolePermission(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(undefined);
    form.resetFields();
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "delete":
        return "Delete";
    }
  }

  const actionsOptions = async (): Promise<InputObject[]> => {
    const options = Object.entries(PermissionActionType).map(
      ([value, display]) => ({
        display,
        value,
      }),
    );

    return options;
  };

  const permissionRequest = (search: string) => {
    return retry(() =>
      getPermissions({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Permission>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        subDisplay: dataItem.tenantName,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const userRolesRequest = (search: string) => {
    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyRole>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        subDisplay: dataItem.tenantName,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Permission", undefined)}
      onCancel={onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type={"primary"}
            danger={props.mode === "delete"}
            loading={loading}
            disabled={!valid && props.mode !== "delete"}
            onClick={onSubmit}
          >
            {createMode()}
          </Button>
        </Space>
      }
    >
      {props.mode === "add" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "companyRole",
                    label: "Company Role",
                    request: userRolesRequest,
                    rules: [
                      {
                        required: true,
                        message: "Company Role is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "action",
                    label: "Action",
                    request: actionsOptions,
                    rules: [
                      {
                        required: true,
                        message: "Action is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "permission",
                    label: "Permission",
                    request: permissionRequest,
                    rules: [
                      {
                        required: true,
                        message: "Permission is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "update" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "action",
                    label: "Action",
                    request: actionsOptions,
                    rules: [
                      {
                        required: true,
                        message: "Action is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <Typography.Paragraph>
          <Typography.Text>
            Continue deleting the{" "}
            <Typography.Text className="font-bold">
              {props.initialValues?.companyRoleName}
            </Typography.Text>{" "}
            permissions?
          </Typography.Text>
        </Typography.Paragraph>
      )}
      {errorMessage && (
        <Alert className="mb-2" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

export default CompanyPermissionsFormDialog;
